/**
 * The drawings a FORM needs and no screen owns: a tick, a bin, a disclosure chevron, the dismiss
 * cross and a copy mark.
 *
 * All three came out of `pages/exercise-library/ui/ExerciseLibraryIcons` on their second consumer
 * (`features/create-exercise`), per `CLAUDE.md`'s promote-on-the-second rule. They are the mock's
 * own `ICON.check`, `ICON.trash` and `ICON.chevD` — the same paths at the same stroke, so the
 * library's filter panel, the theatre's ⋯ menu and the creator's nine metric rows cannot drift into
 * three slightly different ticks.
 *
 * 16-box, `currentColor`, `aria-hidden`: the control around a glyph carries the name, always.
 * The CALLER supplies the size, because the same tick is 12px inside a metric box and 10px in the
 * corner of an equipment tile.
 */

interface GlyphProps {
  readonly className?: string;
}

/** The tick inside a checked box. Drawn rather than typed, so it sits on the box's own baseline. */
export function CheckGlyph({ className }: GlyphProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <path d="m3.5 8.5 3 3 6-7" />
    </svg>
  );
}

/**
 * Dismiss — the cross in the corner of a dialog's head.
 *
 * Promoted here on its second consumer (`features/create-plan` beside `features/assign-plan`), the
 * two windows that wear `shared/ui/splitDialog`. The skin is that sheet's `.close`; this is only
 * the drawing, so the two heads cannot end up with two slightly different crosses.
 */
export function CloseGlyph({ className }: GlyphProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden
      focusable="false"
    >
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

/** Delete — one row of a list, never a whole record. */
export function TrashGlyph({ className }: GlyphProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <path d="M3 4.5h10M6.5 2.5h3M5 4.5l.6 8.5h4.8l.6-8.5" />
    </svg>
  );
}

/** A disclosure's state, pointing down while it is closed. The caller rotates it when it opens. */
export function ChevronDownGlyph({ className }: GlyphProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <path d="m3.5 6 4.5 4.5L12.5 6" />
    </svg>
  );
}

/**
 * Two sheets, one behind the other — a copy control: the trainer code on `/konto`, a day's hours on
 * `/rezerwacje`'s week. Moved here from `pages/account` on its second consumer; a 24-box drawing.
 */
export function CopyGlyph({ className }: GlyphProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="9" width="11" height="12" rx="2.5" />
      <path d="M15.5 5.5A2.5 2.5 0 0 0 13 3H6.5A2.5 2.5 0 0 0 4 5.5V15" />
    </svg>
  );
}
